/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Checkbox, Typography } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import { Namespaces } from '../../../i18n';
import { useTableStyles } from '../../../styles/table';
import { EntityId } from '../../../types/api/EntityId';
import { IProjectWithClusters } from '../../../types/project/IProjectWithClusters';
import { useOrganizationPermissions } from '../../../utils/useOrganizationPermissions';
import { AlignCenter } from '../../AlignCenter/AlignCenter';
import { createProjectsListStyles } from './OrganizationProjectsList.styles';

type ProjectsListProps = {
  projects: IProjectWithClusters[];
  onClick?: (project: IProjectWithClusters) => void;
  onManageClick?: (project: IProjectWithClusters) => void;
  selectedProjects: EntityId[];
  toggleSelectedProject: (projectId: EntityId, checked: boolean) => any;
};

export const OrganizationManageProjectsList = memo<ProjectsListProps>(props => {
  const { t } = useTranslation(Namespaces.OrganizationAdmin);
  const classes = createProjectsListStyles();
  const tableClasses = useTableStyles();
  const { projects, onClick, selectedProjects, toggleSelectedProject, onManageClick } = props;
  const params = useParams<{ organization_id: string }>();
  const organizationId = params.organization_id!;
  const permissions = useOrganizationPermissions(organizationId);
  const onRowClick = onClick ?? (() => {});
  const onManageRowClick = onManageClick ?? null;

  return (
    <TableContainer>
      <Table aria-label="projects table" className={classes.root}>
        <TableHead>
          <TableRow>
            <TableCell classes={{ root: tableClasses.headerTableCell }}>
              {t('management.projects_list.title_projects')}
            </TableCell>
            <TableCell align="right" classes={{ root: tableClasses.headerTableCell }}>
              {t('management.projects_list.title_services')}
            </TableCell>
            <TableCell classes={{ root: tableClasses.headerTableCell }}>
              {t('management.projects_list.title_created')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {projects.map(project => (
            <TableRow
              key={project.id}
              className={cx({
                [tableClasses.rowChecked]: selectedProjects.indexOf(project.id) > -1,
              })}
            >
              <TableCell
                classes={{
                  root: cx(tableClasses.bodyTableCell, tableClasses.checkboxTableCell),
                }}
              >
                <div className={tableClasses.checkboxCell}>
                  <Checkbox
                    inputProps={{
                      'aria-label': t(`${Namespaces.Common}:label.select_proj`, {
                        id: project.id,
                      }),
                    }}
                    name="isChecked"
                    color="primary"
                    icon={<RadioButtonUncheckedIcon />}
                    checkedIcon={<CheckCircleIcon />}
                    checked={selectedProjects.indexOf(project.id) > -1}
                    onChange={e => {
                      if (permissions.edit_organization) {
                        toggleSelectedProject(project.id, e.target.checked);
                      }
                    }}
                    disabled={!permissions.edit_organization}
                  />
                  <Typography
                    classes={{ root: classes.link }}
                    onClick={() => {
                      if (permissions.edit_organization) {
                        onRowClick(project);
                      }
                    }}
                    component="p"
                    className={tableClasses.bodyTableCellText}
                  >
                    {project.name}
                  </Typography>
                </div>
              </TableCell>
              <TableCell align="right" classes={{ root: tableClasses.bodyTableCell }}>
                <Typography component="p" className={tableClasses.bodyTableCellText}>
                  {project.clusters.length}
                </Typography>
              </TableCell>
              <TableCell classes={{ root: tableClasses.bodyTableCell }}>
                <Typography component="p" className={tableClasses.bodyTableCellText}>
                  {t(`${Namespaces.Common}:dates.long_date`, {
                    date: project.createdAt,
                  })}
                </Typography>
              </TableCell>
              {onManageRowClick && permissions.edit_organization && (
                <TableCell
                  classes={{
                    root: cx(classes.editColumn, tableClasses.bodyTableCell),
                  }}
                >
                  <div
                    aria-label={t(`${Namespaces.Common}:label.manage_proj`, {
                      id: project.id,
                    })}
                    className={classes.editIcon}
                    onClick={() => onManageRowClick(project)}
                    data-cy="projects-tab-project-settings-button"
                  />
                </TableCell>
              )}
            </TableRow>
          ))}
          {!projects.length && (
            <TableRow>
              <TableCell colSpan={4} classes={{ root: tableClasses.bodyTableCell }}>
                <AlignCenter>
                  <Typography component="p" className={tableClasses.bodyTableCellText}>
                    {t('management.projects_list.no_projects')}
                  </Typography>
                </AlignCenter>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
});
